import { apiClient } from '@/services/apiClient';

/**
 * Google Business Profile, per account — DIRECT to Google.
 *
 * Zernio is not involved in anything in this file. It remains the social
 * publishing integration (Instagram, Facebook, TikTok, LinkedIn); locations,
 * reviews and replies here travel over the account's own Google OAuth grant,
 * held server-side. The browser only ever sees an authUrl and, on return, the
 * `gb` result parameters.
 */

// Same shape as zernio.service.ts — the v5 envelope is { data: { ... } }.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const unwrap = (r: any) => r.data.data;

/** A Google location linked to this Prospera account. */
export interface LinkedGoogleLocation {
  /** Google's resource name, "locations/456". Opaque; pass it back verbatim. */
  id: string;
  /** "accounts/123" — the Google account the listing lives under. */
  googleAccountName: string;
  name: string;
  address: string;
  storeCode: string;
  /** Prospera facility this listing represents. 0 = account-wide. */
  facilityId: number;
}

export interface GoogleBusinessStatus {
  /** Whether an OAuth client exists to connect against at all (deployment). */
  googleConfigured: boolean;
  connected: boolean;
  /** The Google user who consented, for a "Connected as …" label. */
  connectedAs: string | null;
  /** There IS a grant and Google is refusing it — reconnect, not first setup. */
  needsReconnect: boolean;
  locations: LinkedGoogleLocation[];
}

/** One Google Business location offered during the connect flow. */
export interface GoogleBusinessLocation {
  /** "locations/456" */
  id: string;
  name: string;
  /** Google's account resource name, "accounts/123" — passed back on select. */
  accountId: string;
  accountName: string;
  address: string;
  category: string;
  storeCode: string;
}

export function getGoogleBusinessStatus(): Promise<GoogleBusinessStatus> {
  return apiClient.get('/google-business/status').then(unwrap);
}

/**
 * Begin the OAuth flow.
 *
 * `redirectUrl` must be absolute and on our own origin — the API re-checks it,
 * because it is sealed into the OAuth state and is where Google sends the
 * browser back to.
 */
export function getGoogleBusinessConnectUrl(
  redirectUrl: string,
): Promise<{ authUrl: string }> {
  return apiClient.post('/google-business/connect-url', { redirectUrl }).then(unwrap);
}

/** Forget the grant and every linked location, revoking consent at Google. */
export function disconnectGoogleBusiness(): Promise<{ disconnected: boolean }> {
  return apiClient.post('/google-business/disconnect').then(unwrap);
}

/**
 * The listings the connected Google user administers.
 *
 * `reason: 'no_google_locations'` with an empty list is a real answer, not a
 * failure: that Google account manages no Business Profiles.
 */
export function listGoogleBusinessLocations(
  search = '',
): Promise<{ locations: GoogleBusinessLocation[]; hasMore: boolean; reason?: string }> {
  return apiClient
    .get('/google-business/locations', { params: { ...(search ? { search } : {}) } })
    .then(unwrap);
}

/** Link one Google listing to this account. Repeatable; re-selecting updates. */
export function selectGoogleBusinessLocation(input: {
  locationId: string;
  googleAccountName: string;
  name?: string;
  address?: string;
  storeCode?: string;
  facilityId?: number;
}): Promise<{ connected: boolean; locations: LinkedGoogleLocation[] }> {
  return apiClient.post('/google-business/select-location', input).then(unwrap);
}

/** Unlink a listing. The reviews stay at Google; only our mapping goes. */
export function unlinkGoogleBusinessLocation(
  locationId: string,
): Promise<{ locations: LinkedGoogleLocation[] }> {
  return apiClient
    .delete(`/google-business/locations/${encodeURIComponent(locationId)}`)
    .then(unwrap);
}

/**
 * Connect failures, in words a user can act on.
 *
 * These are OUR callback's reasons now, not a vendor's open-ended list, so the
 * set is closed — but an unrecognised code still falls through to something
 * sayable rather than showing a raw token.
 */
export function googleConnectError(code: string | null): string {
  switch (code) {
    case 'access_denied':
      return 'The Google sign-in was cancelled, so nothing was connected.';
    case 'no_google_locations':
      return 'That Google account manages no business locations. Sign in with the account that owns your Business Profile.';
    case 'no_refresh_token':
      return 'Google did not return a lasting permission. Remove Prospera under your Google Account → Security → Third-party apps, then connect again.';
    case 'expired_state':
      return 'The connection took too long and timed out. Please start again.';
    case 'invalid_state':
    case 'missing_code':
      return 'The connection did not complete. Please try again.';
    default:
      return 'The connection did not complete. Please try again.';
  }
}

// ── Reviews ──────────────────────────────────────────────────────────────────

export interface GoogleReview {
  id: string;
  reviewer: {
    displayName: string;
    profilePhotoUrl: string | null;
    /** An anonymous reviewer must never be addressed by name in a reply. */
    isAnonymous: boolean;
  };
  rating: number;
  comment: string;
  createTime: string;
  updateTime: string;
  reply: { comment: string; updateTime: string } | null;
}

export type ReviewSort = 'newest' | 'oldest' | 'rating_high' | 'rating_low';
export type ReplyStatus = 'all' | 'needs_reply' | 'replied';
export type Sentiment = 'positive' | 'neutral' | 'negative';
export type DatePreset = 'all' | '7' | '30' | '90';

/**
 * Everything the Reviews screen can ask for.
 *
 * All of it is applied by OUR backend, not Google — its reviews endpoint accepts
 * only pageSize, an opaque pageToken and a three-value orderBy, and cannot
 * filter at all. The backend fetches the location's reviews once, caches them,
 * and queries that set.
 */
export interface ReviewQueryParams {
  page?: number;
  pageSize?: number;
  ratings?: number[];
  sentiments?: Sentiment[];
  replyStatus?: ReplyStatus;
  datePreset?: DatePreset;
  dateFrom?: string;
  dateTo?: string;
  sort?: ReviewSort;
}

export interface GoogleReviewsPage {
  reviews: GoogleReview[];
  /** Count AFTER filtering — what "Showing 1–10 of N" must use. */
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  /** The listing's own averages, unaffected by filters. */
  averageRating: number;
  totalReviewCount: number;
  locationId: string;
  /** The listing has more reviews than the fetch cap gathered. */
  truncated: boolean;
}

export function listGoogleReviews(
  locationId: string,
  query: ReviewQueryParams = {},
): Promise<GoogleReviewsPage> {
  return apiClient
    .get('/google-business/reviews', {
      params: {
        locationId,
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 10,
        ...(query.ratings?.length ? { ratings: query.ratings } : {}),
        ...(query.sentiments?.length ? { sentiments: query.sentiments } : {}),
        ...(query.replyStatus && query.replyStatus !== 'all' ? { replyStatus: query.replyStatus } : {}),
        ...(query.datePreset && query.datePreset !== 'all' ? { datePreset: query.datePreset } : {}),
        ...(query.dateFrom ? { dateFrom: query.dateFrom } : {}),
        ...(query.dateTo ? { dateTo: query.dateTo } : {}),
        ...(query.sort ? { sort: query.sort } : {}),
      },
    })
    .then(unwrap);
}

/**
 * Post or replace the owner reply.
 *
 * Google exposes ONE call for both, and keeps no history: this silently
 * destroys whatever reply was there. Confirm before calling it over an existing
 * reply — there is no undo, at Google or here.
 */
export function replyToGoogleReview(
  locationId: string,
  reviewId: string,
  comment: string,
): Promise<{ comment: string; updateTime: string; reviewId: string }> {
  return apiClient
    .post(`/google-business/reviews/${encodeURIComponent(reviewId)}/reply`, { locationId, comment })
    .then(unwrap);
}

export function deleteGoogleReviewReply(
  locationId: string,
  reviewId: string,
): Promise<{ success: boolean; reviewId: string }> {
  return apiClient
    .delete(`/google-business/reviews/${encodeURIComponent(reviewId)}/reply`, {
      params: { locationId },
    })
    .then(unwrap);
}

/**
 * Suggest a reply. It publishes NOTHING — the caller posts it separately, after
 * the user has read and edited it. The reviewer's name is never sent.
 */
export function draftGoogleReviewReply(
  rating: number,
  comment: string,
): Promise<{ draft: string; notice: string }> {
  return apiClient
    .post('/google-business/reviews/draft-reply', { rating, comment })
    .then(unwrap);
}

// ── Performance ──────────────────────────────────────────────────────────────

export interface GooglePerformance {
  metrics: Record<string, { total: number; values: Array<{ date: string; value: number }> }>;
  dateRange: { startDate: string; endDate: string };
  /** Google's own lag. Shown on the page, because today's zero is not a zero. */
  dataDelay: string;
}

export function getGooglePerformance(
  locationId: string,
  days = 30,
): Promise<GooglePerformance> {
  return apiClient
    .get('/google-business/performance', { params: { locationId, days } })
    .then(unwrap);
}

/**
 * Impressions, summed across the four Maps/Search x desktop/mobile splits.
 *
 * Deliberately NOT called "profile views": impressions count the listing
 * appearing in results, which is a larger and different number. Naming it views
 * would overstate performance on every single report.
 */
export function totalImpressions(p: GooglePerformance): number {
  return Object.entries(p.metrics)
    .filter(([name]) => name.startsWith('BUSINESS_IMPRESSIONS_'))
    .reduce((sum, [, m]) => sum + m.total, 0);
}
